import React, { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, Search, CheckCircle, XCircle, Printer, Eye, Download } from 'lucide-react';
import vendaService, { VendaResponseDTO } from '../services/vendaService';
import { PageResponse } from '../types/api';
import alertService from '../services/alertService';
import { useNavigate } from 'react-router-dom';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

const formatDateTime = (iso?: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return iso;
  }
};

const formatDate = (iso?: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(d);
  } catch {
    return iso;
  }
};

const VendasPage: React.FC = () => {
  // Filtros e paginação
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [status, setStatus] = useState<string>('');
  const [vendaId, setVendaId] = useState<string>('');
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  // Estado de dados
  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState<PageResponse<VendaResponseDTO> | null>(null);
  const navigate = useNavigate();

  const abrirPDV = () => {
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      '/pdv',
      'PDV',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
    );
  };

  const listarVendas = async () => {
    setLoading(true);
    try {
      const id = vendaId?.trim();
      if (id) {
        const r = await vendaService.obter(id);
        const payload: any = r.data;
        const venda = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload;
        const pageResp: PageResponse<VendaResponseDTO> = venda
          ? { content: [venda as VendaResponseDTO], totalElements: 1, totalPages: 1, size: 1, number: 0, first: true, last: true }
          : { content: [], totalElements: 0, totalPages: 0, size: 0, number: 0, first: true, last: true };
        setPageData(pageResp);
        return;
      }

      const usePeriodo = !!dataInicio && !!dataFim;
      const response = usePeriodo
        ? await vendaService.listarPorPeriodo({
            page,
            size,
            status: status ? status : undefined,
            dataVendaInicio: dataInicio || undefined,
            dataVendaFim: dataFim || undefined,
          })
        : await vendaService.listar({
            page,
            size,
            status: status ? status : undefined,
          });
      // Normalizar resposta: pode vir PageResponse direto ou dentro de ApiResponse
      const data: any = response?.data;
      const pageResp = data && data.content
        ? (data as PageResponse<VendaResponseDTO>)
        : (data && data.data && data.data.content
            ? (data.data as PageResponse<VendaResponseDTO>)
            : ({ content: [], totalElements: 0, totalPages: 0, size: 0, number: 0, first: true, last: true } as PageResponse<VendaResponseDTO>)
          );
      setPageData(pageResp);
    } catch (err: any) {
      alertService.error('Falha ao listar vendas', err?.message || 'Verifique a conexão ou credenciais');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listarVendas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, status, dataInicio, dataFim, vendaId]);

  const canPrev = useMemo(() => !!pageData && !pageData.first && page > 0, [pageData, page]);
  const canNext = useMemo(() => !!pageData && !pageData.last && page < (pageData?.totalPages || 1) - 1, [pageData, page]);

  const finalizarVenda = async (id: string) => {
    const confirm = await alertService.confirm('Finalizar venda?', 'Confirmando, a venda será marcada como PAGA.');
    if (!confirm.isConfirmed) return;
    try {
      alertService.loading('Finalizando venda...');
      await vendaService.finalizar(id);
      alertService.close();
      alertService.success('Venda finalizada com sucesso');
      listarVendas();
    } catch (err: any) {
      alertService.close();
      alertService.error('Erro ao finalizar venda', err?.message || 'Tente novamente mais tarde');
    }
  };

  const cancelarVenda = async (id: string) => {
    const confirm = await alertService.confirm('Cancelar venda?', 'Esta ação moverá a venda para status CANCELADA.');
    if (!confirm.isConfirmed) return;
    try {
      alertService.loading('Cancelando venda...');
      await vendaService.cancelar(id);
      alertService.close();
      alertService.success('Venda cancelada');
      listarVendas();
    } catch (err: any) {
      alertService.close();
      alertService.error('Erro ao cancelar venda', err?.message || 'Tente novamente mais tarde');
    }
  };

  const abrirCupom = (id: string) => {
    window.open(`/pdv/cupom/${id}`, '_blank');
  };

  return (
    <div>
      {/* Topo com ação PDV */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <ShoppingCart className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Vendas</h1>
            <p className="text-sm sm:text-base text-gray-600">Gerencie as vendas do sistema</p>
          </div>
        </div>
        <button
          onClick={abrirPDV}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors w-full sm:w-auto"
        >
          <ShoppingCart className="h-4 w-4" />
          <span className="sm:inline">Abrir PDV</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Id da Venda</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                value={vendaId}
                onChange={(e) => setVendaId(e.target.value)}
                placeholder="Opcional"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="PENDENTE">PENDENTE</option>
              <option value="FINALIZADA">FINALIZADA</option>
              <option value="CANCELADA">CANCELADA</option>
              <option value="EM_PROCESSAMENTO">EM_PROCESSAMENTO</option>
              <option value="ENTREGUE">ENTREGUE</option>
              <option value="PARCIALMENTE_ENTREGUE">PARCIALMENTE_ENTREGUE</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data início</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data fim</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div className="flex flex-wrap items-end gap-2 justify-start md:justify-end md:col-span-4">
            <button
              onClick={() => { setPage(0); listarVendas(); }}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors w-full md:w-auto whitespace-nowrap"
            >
              Buscar
            </button>
            <button
              onClick={() => { setVendaId(''); setStatus(''); setDataInicio(''); setDataFim(''); setPage(0); listarVendas(); }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 w-full md:w-auto whitespace-nowrap"
            >
              Limpar filtros
            </button>
            <button
              onClick={async () => {
                try {
                  if (!pageData) return alertService.info('Nada a exportar');
                  const total = pageData.totalElements || 0;
                  alertService.loading('Gerando arquivo...');
                  const id = vendaId?.trim();
                  let rows: VendaResponseDTO[] = [];
                  if (id) {
                    const r = await vendaService.obter(id);
                    const payload: any = r.data;
                    const venda = (payload && typeof payload === 'object' && 'data' in payload) ? payload.data : payload;
                    rows = venda ? [venda as VendaResponseDTO] : [];
                  } else {
                    const usePeriodo = !!dataInicio && !!dataFim;
                    const resp = usePeriodo
                      ? await vendaService.listarPorPeriodo({
                          page: 0,
                          size: Math.max(total, size),
                          status: status || undefined,
                          dataVendaInicio: dataInicio || undefined,
                          dataVendaFim: dataFim || undefined,
                        })
                      : await vendaService.listar({
                          page: 0,
                          size: Math.max(total, size),
                          status: status || undefined,
                        });
                    const all = (resp.data as any).content || ((resp.data as any)?.data?.content) || [];
                    rows = all as VendaResponseDTO[];
                  }
                  const sep = ';';
                  const headers = ['ID','Cliente','Vendedor','Valor Total','Status','Data da venda','Criado em'];
                  const csvLines = [headers.join(sep), ...rows.map(r => [
                    r.id,
                    r.clienteNome || r.clienteId || '',
                    (r as any).vendedorNome || (r as any).vendedorId || '',
                    String(r.valorTotal).replace('.', ','),
                    r.status,
                    (r as any).dataVenda ? formatDate((r as any).dataVenda) : '',
                    r.criadoEm ? formatDateTime(r.criadoEm) : ''
                  ].map(v => (String(v).includes(sep) || String(v).includes('\n')) ? `"${String(v).replace(/"/g,'""')}"` : String(v)).join(sep))];
                  const bom = '\ufeff';
                  const blob = new Blob([bom + csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const now = new Date();
                  const pad = (n: number) => n.toString().padStart(2, '0');
                  const fname = `vendas_${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}.csv`;
                  a.download = fname;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  alertService.close();
                } catch (e: any) {
                  alertService.close();
                  alertService.error('Falha ao exportar vendas', e?.message || 'Tente novamente');
                }
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 w-full md:w-auto whitespace-nowrap"
              title="Exportar para Excel"
            >
              <Download className="h-4 w-4" /> Exportar Excel
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendedor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data da venda</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td className="px-6 py-6 text-center text-gray-600" colSpan={8}>Carregando vendas...</td>
                </tr>
              )}
              {!loading && pageData && (pageData.content?.length || 0) === 0 && (
                <tr>
                  <td className="px-6 py-6 text-center text-gray-600" colSpan={8}>Nenhuma venda encontrada.</td>
                </tr>
              )}
              {!loading && pageData?.content?.map((v) => (
                <tr key={v.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <a href={`/vendas/${v.id}`} className="text-indigo-600 hover:underline">{v.id}</a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{v.clienteNome || v.clienteId || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{(v as any).vendedorNome || (v as any).vendedorId || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(v.valorTotal)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_STYLES[v.status] || 'bg-gray-100 text-gray-700'}`} title={STATUS_DESCRIPTIONS[v.status] || v.status}>{v.status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDate((v as any).dataVenda)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{formatDateTime(v.criadoEm)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                     <div className="flex justify-end gap-2">
                       <button
                         onClick={() => navigate(`/vendas/${v.id}`)}
                        className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                        title="Detalhe da venda"
                      >
                        <Eye className="h-4 w-4" /> Detalhe
                      </button>
                       <button
                         onClick={() => abrirCupom(v.id)}
                        className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                        title="Imprimir cupom"
                      >
                        <Printer className="h-4 w-4" /> Cupom
                      </button>
                      {['PENDENTE','EM_PROCESSAMENTO'].includes(v.status) && (
                        <>
                          <button
                            onClick={() => finalizarVenda(v.id)}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1"
                          >
                            <CheckCircle className="h-4 w-4" /> Finalizar
                          </button>
                          <button
                            onClick={() => cancelarVenda(v.id)}
                            className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-1"
                          >
                            <XCircle className="h-4 w-4" /> Cancelar
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      {/* Paginação */}
        { (pageData?.totalElements || 0) > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {(() => {
                const total = pageData?.totalElements || 0;
                const start = page * size + 1;
                const end = Math.min((page + 1) * size, total);
                return `Mostrando ${start} a ${end} de ${total} vendas`;
              })()}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  setPage(0);
                }}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm">
                Página {page + 1} de {pageData?.totalPages || 1}
              </span>
              <button
                onClick={() => setPage(Math.min((pageData?.totalPages || 1) - 1, page + 1))}
                disabled={page >= (pageData?.totalPages || 1) - 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendasPage;

const STATUS_STYLES: Record<string, string> = {
  PENDENTE: 'bg-yellow-100 text-yellow-700',
  FINALIZADA: 'bg-green-100 text-green-700',
  CANCELADA: 'bg-red-100 text-red-700',
  EM_PROCESSAMENTO: 'bg-blue-100 text-blue-700',
  ENTREGUE: 'bg-emerald-100 text-emerald-700',
  PARCIALMENTE_ENTREGUE: 'bg-orange-100 text-orange-700',
};

const STATUS_DESCRIPTIONS: Record<string, string> = {
  PENDENTE: 'Venda criada, aguardando finalização',
  FINALIZADA: 'Venda finalizada e estoque atualizado',
  CANCELADA: 'Venda cancelada',
  EM_PROCESSAMENTO: 'Venda sendo processada',
  ENTREGUE: 'Produtos entregues ao cliente',
  PARCIALMENTE_ENTREGUE: 'Entrega parcial dos produtos',
};